import {
  SpeechPacingRequestDto,
  SpeechPacingResponseDto,
  TurnHandlingRequestDto,
  TurnHandlingResponseDto,
} from '@shared/services/apiService/apiService';

export type DelayDraft = {
  mode: 'scalar' | 'range';
  scalar: string;
  min: string;
  max: string;
};

export type PreemptiveGenerationDraft = {
  enabled: boolean;
  preemptiveTts: boolean;
  maxSpeechDuration: string;
  maxRetries: string;
};

export type TurnHandlingDraft = {
  minEndpointingDelay: string;
  maxEndpointingDelay: string;
  turnDetection: string;
  endpointingMode?: 'fixed' | 'dynamic';
  interruptionEnabled?: boolean;
  interruptionMode?: 'vad' | 'adaptive';
  minInterruptionWords: string;
  minInterruptionDuration: string;
  falseInterruptionTimeout: string;
  resumeFalseInterruption?: boolean;
  preemptiveGeneration?: PreemptiveGenerationDraft;
};

export type SpeechPacingDraft = {
  enabled: boolean;
  phraseBoundaryDelayMs: DelayDraft;
  sentenceBoundaryDelayMs: DelayDraft;
  seed: string;
};

export type ConversationSettingsDraft = {
  turnHandling: TurnHandlingDraft | null;
  speechPacing: SpeechPacingDraft | null;
};

export type ConversationSettingsValidation = {
  turnHandling: Partial<Record<keyof TurnHandlingDraft | string, string>>;
  speechPacing: Partial<Record<keyof SpeechPacingDraft | string, string>>;
};

const emptyDelay = (): DelayDraft => ({ mode: 'scalar', scalar: '', min: '', max: '' });

const numberToString = (value?: number) => (value === undefined ? '' : String(value));

const mapDelayToDraft = (value?: number | { minMs: number; maxMs: number }): DelayDraft => {
  if (value && typeof value === 'object') {
    return {
      mode: 'range',
      scalar: '',
      min: numberToString(value.minMs),
      max: numberToString(value.maxMs),
    };
  }
  return { mode: 'scalar', scalar: numberToString(value), min: '', max: '' };
};

const mapPreemptiveToDraft = (
  value?: TurnHandlingResponseDto['preemptiveGeneration'],
): PreemptiveGenerationDraft | undefined =>
  value
    ? {
        enabled: Boolean(value.enabled),
        preemptiveTts: Boolean(value.preemptiveTts),
        maxSpeechDuration: numberToString(value.maxSpeechDuration),
        maxRetries: numberToString(value.maxRetries),
      }
    : undefined;

export const mapConversationSettingsToDraft = (
  turnHandling: TurnHandlingResponseDto | null | undefined,
  speechPacing: SpeechPacingResponseDto | null | undefined,
): ConversationSettingsDraft => ({
  turnHandling:
    turnHandling === null || turnHandling === undefined || Object.keys(turnHandling).length === 0
      ? null
      : {
          minEndpointingDelay: numberToString(turnHandling.minEndpointingDelay),
          maxEndpointingDelay: numberToString(turnHandling.maxEndpointingDelay),
          turnDetection: turnHandling.turnDetection ?? '',
          endpointingMode: turnHandling.endpointingMode,
          interruptionEnabled: turnHandling.interruptionEnabled,
          interruptionMode: turnHandling.interruptionMode,
          minInterruptionWords: numberToString(turnHandling.minInterruptionWords),
          minInterruptionDuration: numberToString(turnHandling.minInterruptionDuration),
          falseInterruptionTimeout: numberToString(turnHandling.falseInterruptionTimeout),
          resumeFalseInterruption: turnHandling.resumeFalseInterruption,
          preemptiveGeneration: mapPreemptiveToDraft(turnHandling.preemptiveGeneration),
        },
  speechPacing:
    speechPacing === null || speechPacing === undefined
      ? null
      : {
          enabled: Boolean(speechPacing.enabled),
          phraseBoundaryDelayMs: mapDelayToDraft(speechPacing.phraseBoundaryDelayMs),
          sentenceBoundaryDelayMs: mapDelayToDraft(speechPacing.sentenceBoundaryDelayMs),
          seed: speechPacing.seed ?? '',
        },
});

const numberValue = (value: string) => (value.trim() === '' ? undefined : Number(value));

const addNumber = (target: Record<string, unknown>, key: string, value: string) => {
  const parsed = numberValue(value);
  if (parsed !== undefined && Number.isFinite(parsed)) target[key] = parsed;
};

const mapDelayToRequest = (
  draft: DelayDraft,
): number | { minMs: number; maxMs: number } | undefined => {
  if (draft.mode === 'range') {
    const min = numberValue(draft.min);
    const max = numberValue(draft.max);
    return min !== undefined && max !== undefined ? { minMs: min, maxMs: max } : undefined;
  }
  return numberValue(draft.scalar);
};

export const mapConversationSettingsDraftToRequest = (
  draft: ConversationSettingsDraft,
): {
  turnHandling: TurnHandlingRequestDto | null;
  speechPacing: SpeechPacingRequestDto | null;
} => {
  if (draft.turnHandling === null) {
    return { turnHandling: null, speechPacing: mapSpeechPacing(draft.speechPacing) };
  }

  const turn: Record<string, unknown> = {};
  addNumber(turn, 'minEndpointingDelay', draft.turnHandling.minEndpointingDelay);
  addNumber(turn, 'maxEndpointingDelay', draft.turnHandling.maxEndpointingDelay);
  if (draft.turnHandling.turnDetection.trim())
    turn.turnDetection = draft.turnHandling.turnDetection;
  if (draft.turnHandling.endpointingMode) turn.endpointingMode = draft.turnHandling.endpointingMode;
  if (draft.turnHandling.interruptionEnabled !== undefined) {
    turn.interruptionEnabled = draft.turnHandling.interruptionEnabled;
  }
  if (draft.turnHandling.interruptionMode)
    turn.interruptionMode = draft.turnHandling.interruptionMode;
  addNumber(turn, 'minInterruptionWords', draft.turnHandling.minInterruptionWords);
  addNumber(turn, 'minInterruptionDuration', draft.turnHandling.minInterruptionDuration);
  addNumber(turn, 'falseInterruptionTimeout', draft.turnHandling.falseInterruptionTimeout);
  if (draft.turnHandling.resumeFalseInterruption !== undefined) {
    turn.resumeFalseInterruption = draft.turnHandling.resumeFalseInterruption;
  }
  if (draft.turnHandling.preemptiveGeneration) {
    const preemptive: Record<string, unknown> = {};
    preemptive.enabled = draft.turnHandling.preemptiveGeneration.enabled;
    preemptive.preemptiveTts = draft.turnHandling.preemptiveGeneration.preemptiveTts;
    addNumber(
      preemptive,
      'maxSpeechDuration',
      draft.turnHandling.preemptiveGeneration.maxSpeechDuration,
    );
    addNumber(preemptive, 'maxRetries', draft.turnHandling.preemptiveGeneration.maxRetries);
    turn.preemptiveGeneration = preemptive;
  }

  return {
    turnHandling: Object.keys(turn).length > 0 ? (turn as TurnHandlingRequestDto) : null,
    speechPacing: mapSpeechPacing(draft.speechPacing),
  };
};

const mapSpeechPacing = (draft: SpeechPacingDraft | null): SpeechPacingRequestDto | null => {
  if (draft === null) return null;
  const request: SpeechPacingRequestDto = { enabled: draft.enabled };
  const phrase = mapDelayToRequest(draft.phraseBoundaryDelayMs);
  const sentence = mapDelayToRequest(draft.sentenceBoundaryDelayMs);
  if (phrase !== undefined) request.phraseBoundaryDelayMs = phrase;
  if (sentence !== undefined) request.sentenceBoundaryDelayMs = sentence;
  if (draft.seed.trim()) request.seed = draft.seed.trim();
  return request;
};

const invalidNumber = (value: string, integer = false) => {
  if (!value.trim()) return undefined;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0 || (integer && !Number.isInteger(parsed))) {
    return 'conversationSettings.validation.nonNegative';
  }
  return undefined;
};

const validateDelay = (draft: DelayDraft, prefix: string) => {
  const errors: Record<string, string> = {};
  if (draft.mode === 'range') {
    errors[`${prefix}.min`] = invalidNumber(draft.min) ?? '';
    errors[`${prefix}.max`] = invalidNumber(draft.max) ?? '';
    if (
      !errors[`${prefix}.min`] &&
      !errors[`${prefix}.max`] &&
      Number(draft.max) < Number(draft.min)
    ) {
      errors[`${prefix}.max`] = 'conversationSettings.validation.range';
    }
  } else {
    errors[prefix] = invalidNumber(draft.scalar) ?? '';
  }
  return Object.fromEntries(Object.entries(errors).filter(([, value]) => value));
};

export const validateConversationSettingsDraft = (
  draft: ConversationSettingsDraft,
): ConversationSettingsValidation => {
  const turnHandling: Record<string, string> = {};
  const speechPacing: Record<string, string> = {};

  if (draft.turnHandling) {
    const numberFields: Array<[keyof TurnHandlingDraft, boolean]> = [
      ['minEndpointingDelay', false],
      ['maxEndpointingDelay', false],
      ['minInterruptionWords', true],
      ['minInterruptionDuration', false],
      ['falseInterruptionTimeout', false],
    ];
    numberFields.forEach(([key, integer]) => {
      const error = invalidNumber(draft.turnHandling![key] as string, integer);
      if (error) turnHandling[key] = error;
    });
    if (draft.turnHandling.preemptiveGeneration) {
      const preemptive = draft.turnHandling.preemptiveGeneration;
      const durationError = invalidNumber(preemptive.maxSpeechDuration);
      const retriesError = invalidNumber(preemptive.maxRetries, true);
      if (durationError) turnHandling.maxSpeechDuration = durationError;
      if (retriesError) turnHandling.maxRetries = retriesError;
    }
    if (
      !turnHandling.minEndpointingDelay &&
      !turnHandling.maxEndpointingDelay &&
      draft.turnHandling.minEndpointingDelay.trim() &&
      draft.turnHandling.maxEndpointingDelay.trim() &&
      Number(draft.turnHandling.minEndpointingDelay) >
        Number(draft.turnHandling.maxEndpointingDelay)
    ) {
      turnHandling.maxEndpointingDelay = 'conversationSettings.validation.range';
    }
  }

  if (draft.speechPacing) {
    Object.assign(
      speechPacing,
      validateDelay(draft.speechPacing.phraseBoundaryDelayMs, 'phraseBoundaryDelayMs'),
    );
    Object.assign(
      speechPacing,
      validateDelay(draft.speechPacing.sentenceBoundaryDelayMs, 'sentenceBoundaryDelayMs'),
    );
    if (draft.speechPacing.enabled && !draft.speechPacing.seed.trim()) {
      speechPacing.seed = 'conversationSettings.validation.seedRequired';
    }
  }

  return { turnHandling, speechPacing };
};

export const cloneConversationSettingsDraft = (
  draft: ConversationSettingsDraft,
): ConversationSettingsDraft => JSON.parse(JSON.stringify(draft));
